import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import { Input } from 'src/components/ui/input';
import type { ConfigRendererProps } from './types';

/**
 * Config renderer for the process_pick_ticket_bol step
 * Allows configuring dry run mode, email notifications, and notification recipient
 */
export function ProcessPickTicketBOLConfigRenderer({
  config,
  onConfigChange,
}: ConfigRendererProps) {
  const sendToFamous = !config.dryRun;
  const includeProductionEstimate = !config.useTestWarehouse;
  const skipLotFields = Boolean(config.skipLotFields);
  const sendNotificationEmail = Boolean(config.sendNotificationEmail);
  const notificationEmailTo = (config.notificationEmailTo as string) || '';

  const handleSendToFamousToggle = (checked: boolean) => {
    onConfigChange({
      ...config,
      dryRun: !checked, // Invert: sendToFamous=true means dryRun=false
    });
  };

  const handleIncludeProductionEstimateToggle = (checked: boolean) => {
    onConfigChange({
      ...config,
      useTestWarehouse: !checked, // Invert: includeProduction=true means useTestWarehouse=false
    });
  };

  const handleSkipLotFieldsToggle = (checked: boolean) => {
    onConfigChange({
      ...config,
      skipLotFields: checked,
    });
  };

  const handleSendNotificationEmailToggle = (checked: boolean) => {
    onConfigChange({
      ...config,
      sendNotificationEmail: checked,
    });
  };

  const handleNotificationEmailToChange = (value: string) => {
    onConfigChange({
      ...config,
      notificationEmailTo: value || undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* Send to Famous Toggle */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="sendToFamous" className="text-sm font-medium">
              Upload to Famous
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Upload the generated CSV to Famous Software
            </p>
          </div>
          <Switch
            id="sendToFamous"
            checked={sendToFamous}
            onCheckedChange={handleSendToFamousToggle}
            aria-label="Upload to Famous"
          />
        </div>
      </div>

      {/* Include Production Estimate Toggle */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label
              htmlFor="includeProductionEstimate"
              className="text-sm font-medium"
            >
              Include Production Estimate
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Create both PTI Labels and production warehouse estimates. When
              off, only creates the PTI Labels estimate.
            </p>
          </div>
          <Switch
            id="includeProductionEstimate"
            checked={includeProductionEstimate}
            onCheckedChange={handleIncludeProductionEstimateToggle}
            aria-label="Include production estimate"
          />
        </div>
      </div>

      {/* Skip Lot Fields Toggle */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="skipLotFields" className="text-sm font-medium">
              Skip Lot Fields
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Leave Lot ID and Lot Description blank in the import
            </p>
          </div>
          <Switch
            id="skipLotFields"
            checked={skipLotFields}
            onCheckedChange={handleSkipLotFieldsToggle}
            aria-label="Skip lot fields"
          />
        </div>
      </div>

      {/* Send Notification Email Toggle */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label
              htmlFor="sendNotificationEmail"
              className="text-sm font-medium"
            >
              Send Notification Email
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Send email notification with CSV attachment when processing
              completes
            </p>
          </div>
          <Switch
            id="sendNotificationEmail"
            checked={sendNotificationEmail}
            onCheckedChange={handleSendNotificationEmailToggle}
            aria-label="Enable notification email"
          />
        </div>
      </div>

      {/* Notification Email To (only shown when sendNotificationEmail is true) */}
      {sendNotificationEmail && (
        <div className="space-y-2 rounded-md border p-3">
          <Label htmlFor="notificationEmailTo" className="text-sm font-medium">
            Notification Email Address
          </Label>
          <Input
            id="notificationEmailTo"
            type="email"
            placeholder="email@example.com"
            value={notificationEmailTo}
            onChange={(e) => handleNotificationEmailToChange(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Email address to receive the notification with the generated CSV.
            Supports comma-separated addresses for multiple recipients.
          </p>
        </div>
      )}
    </div>
  );
}
